/**
 * Reconciliación de miembros faltantes
 * Vincula 31 miembros del CSV oficial que no matchearon por correo,
 * actualizando su correo al oficial y marcándolos como miembro_oficial.
 */

import React, { useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { normalize, calculateSimilarity } from '../../utils/matching';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

// ============ DATA: 31 faltantes con su match en Firestore ============

interface ReconciliationEntry {
  csvNombre: string;
  csvCorreo: string;
  csvTipo: string; // ASOCIADO | AFILIADO
  matchDocId: string; // Firestore doc ID del match por nombre
  matchCollection: string; // asesor | asociado
  matchNombre: string;
  matchCorreo: string; // correo actual (incorrecto/desactualizado)
}

const RECONCILIATION_DATA: ReconciliationEntry[] = [
  // 27 con match por nombre exacto → actualizar correo
  { csvNombre: "Alejandra Vergara", csvCorreo: "ale.vergara@mac.com", csvTipo: "ASOCIADO", matchDocId: "a5WDN4bGYQee2Gp1ykJr", matchCollection: "asociado", matchNombre: "Alejandra Vergara", matchCorreo: "ale.vergara@interamerican.com" },
  { csvNombre: "Argelia García", csvCorreo: "argeliagarciapv@gmail.com", csvTipo: "ASOCIADO", matchDocId: "x5ulgZf949baB2b1UDid", matchCollection: "asociado", matchNombre: "Argelia García", matchCorreo: "argeliagarciapvq@gmail.com" },
  { csvNombre: "Alejandra Wulff", csvCorreo: "ale@wulff.mx", csvTipo: "ASOCIADO", matchDocId: "Phwus7FYAveMaUaO0rjR", matchCollection: "asociado", matchNombre: "Alejandra Wulff", matchCorreo: "alewulffmx@gmail.com" },
  { csvNombre: "Teresa Marie Beckett", csvCorreo: "tbeckett@itsmexico.com", csvTipo: "ASOCIADO", matchDocId: "uUtmDRyLZlAJemjFV00u", matchCollection: "asociado", matchNombre: "Teresa Marie Beckett", matchCorreo: "tbeckett@ltsmexico.com" },
  { csvNombre: "Maria Eugenia Sellerier", csvCorreo: "maru@lionsrealestate.properties", csvTipo: "ASOCIADO", matchDocId: "KJwrtBBhXpTuFQXhIBtO", matchCollection: "asociado", matchNombre: "Maria Eugenia Sellerier", matchCorreo: "maru@lionsrealstate.properties" },
  { csvNombre: "Sophia Mannerheim", csvCorreo: "sophia@sixtenrealty.com", csvTipo: "ASOCIADO", matchDocId: "CTNNdsKs1mawtY0owKeR", matchCollection: "asociado", matchNombre: "Sophia Mannerheim", matchCorreo: "sophia@sixenrealty.com" },
  { csvNombre: "Brando Ernesto Gonzalez García De la Paz", csvCorreo: "brando.gonzalez@kwmexico.mx", csvTipo: "ASOCIADO", matchDocId: "hSbhyo2eaYLvAPz9PgHq", matchCollection: "asociado", matchNombre: "Brando Ernesto Gonzalez García De La Paz", matchCorreo: "bgonzalez@govacasa.com" },
  { csvNombre: "Carlos González Hernandez", csvCorreo: "carlos@yfhinternational.com", csvTipo: "ASOCIADO", matchDocId: "k6cdihc3RaSEsK5ItSad", matchCollection: "asociado", matchNombre: "Carlos González Hernandez", matchCorreo: "car1hdezcar1hdez@gmail.com" },
  { csvNombre: "Gloria Alvarado", csvCorreo: "gloria.alvarado.realestate@gmail.com", csvTipo: "ASOCIADO", matchDocId: "aJAoQjrLbAqNC05Arsij", matchCollection: "asesor", matchNombre: "Gloria Alvarado", matchCorreo: "gloria@remax-kaizen.mx" },
  { csvNombre: "Alberto Briseño Castañeda", csvCorreo: "alberto@abcliving.mx", csvTipo: "ASOCIADO", matchDocId: "wVBMYKouI9zs5gOXHND5", matchCollection: "asesor", matchNombre: "Alberto Briseño Castañeda", matchCorreo: "alberto.briseno@domusvallarta.com" },
  { csvNombre: "Marcela García", csvCorreo: "asesorainmobiliaria@gmail.com", csvTipo: "ASOCIADO", matchDocId: "5J2F7gvhw2fbANZydDzk", matchCollection: "asesor", matchNombre: "Marcela Garcia", matchCorreo: "marcela.garcia@domusvallarta.com" },
  { csvNombre: "Nadia Evelyn Estrada", csvCorreo: "nevelyn.estrada@gmail.com", csvTipo: "ASOCIADO", matchDocId: "HZgi1XVltTUbb4BLIqEm", matchCollection: "asesor", matchNombre: "Nadia Evelyn Estrada", matchCorreo: "evelyn@bayrealtymexico.com" },
  { csvNombre: "Karen Reynoso", csvCorreo: "karen@karenreynosorealestate.com", csvTipo: "ASOCIADO", matchDocId: "mvrMjwr0UT90B0haYrtt", matchCollection: "asesor", matchNombre: "Karen Reynoso", matchCorreo: "karen@maromma.com" },
  { csvNombre: "Miguel Sánchez", csvCorreo: "mspvrentals@gmail.com", csvTipo: "AFILIADO", matchDocId: "CrNxOgJ1ktwrb9mfZrYY", matchCollection: "asesor", matchNombre: "Miguel Sanchez", matchCorreo: "miguelrealtorpv@gmail.com" },
  { csvNombre: "Alessandro Sartor", csvCorreo: "alessandro@vallartalistings.com", csvTipo: "AFILIADO", matchDocId: "86peWrNe2zvoLYnsP4KK", matchCollection: "asesor", matchNombre: "Alessandro Sartor", matchCorreo: "aless.sartor@gmail.com" },
  { csvNombre: "Mireya Elena Bautista Galindo", csvCorreo: "mireya@lionsrealestate", csvTipo: "AFILIADO", matchDocId: "PVDr00ZFdkJg6VVZszLS", matchCollection: "asesor", matchNombre: "Mireya Elena Bautista Galindo", matchCorreo: "mireya@lionsrealstate.properties" },
  { csvNombre: "Juan Pablo Mora Ibarra", csvCorreo: "juanpablo@nsranpancho.com", csvTipo: "AFILIADO", matchDocId: "ttJiOC4sId8JVYH6FBRM", matchCollection: "asesor", matchNombre: "Juan Pablo Mora Ibarra", matchCorreo: "juanpablo@nsrsanpancho.com" },
  { csvNombre: "Rosa Luz Morales Rodríguez", csvCorreo: "luz.fenixvallarta@gmail.com", csvTipo: "AFILIADO", matchDocId: "Eo0ISXDUfwEfrGuBDvld", matchCollection: "asesor", matchNombre: "Rosa Luz Morales Rodriguez", matchCorreo: "rosalus@cbsvallarta.com" },
  { csvNombre: "Beatriz Zamora", csvCorreo: "beatriz@casaluxerealestate.com", csvTipo: "AFILIADO", matchDocId: "rxAOD3Dg03Dx1R8772cb", matchCollection: "asesor", matchNombre: "Beatriz Zamora", matchCorreo: "beatriz@casaluxerealstate.com" },
  { csvNombre: "Monica Zamudio", csvCorreo: "monica.zaudio@domusvallarta.com", csvTipo: "AFILIADO", matchDocId: "6WSYyhpjYbDg8rSJuq1m", matchCollection: "asesor", matchNombre: "Monica Zamudio", matchCorreo: "monica.zamudio@domusvallarta.com" },
  { csvNombre: "Christopher Vincent Bouchard", csvCorreo: "chris@remaxonthebay.net", csvTipo: "AFILIADO", matchDocId: "9zJNfcRbwQy56CpMMc7n", matchCollection: "asesor", matchNombre: "Christopher Vincent Bouchard", matchCorreo: "chris@remaxplayalacruz.com" },
  { csvNombre: "Cecilia Aldrete", csvCorreo: "cecy@remaxpuertovallarta.com", csvTipo: "AFILIADO", matchDocId: "R9Zc7JNqvyWiFP5C2C58", matchCollection: "asesor", matchNombre: "Cecilia Aldrete", matchCorreo: "cecy@remaxpuertovalalrta.com" },
  { csvNombre: "Cameron Fitzgerald", csvCorreo: "cameron@remaxpuertovallarta.com", csvTipo: "AFILIADO", matchDocId: "2OeAZQpzrtY6iJI3mT9X", matchCollection: "asesor", matchNombre: "Cameron Fitzgerald", matchCorreo: "cameron.fitzgerald@yahoo.com" },
  { csvNombre: "Kiki Cheung", csvCorreo: "kiki@protierrapartners.com", csvTipo: "AFILIADO", matchDocId: "YQJXF1yLjtMeeWvnK9zK", matchCollection: "asesor", matchNombre: "Kiki Cheung", matchCorreo: "kiki@pacificoproperty.com" },
  { csvNombre: "Laura Daniela Marrón Flores", csvCorreo: "daniela@northshoreralty.com", csvTipo: "AFILIADO", matchDocId: "mdEAzrelprq1KbOK3kEk", matchCollection: "asesor", matchNombre: "Laura Daniela Marrón Flores", matchCorreo: "daniela@northshorerealty.com" },
  { csvNombre: "Luis Bobadilla", csvCorreo: "bobadilla19@gmail.com", csvTipo: "AFILIADO", matchDocId: "AKp156YXPHgWy3bLgoeJ", matchCollection: "asesor", matchNombre: "Luis Bobadilla", matchCorreo: "luis.bobadilla@as-realestate.com.mx" },
  { csvNombre: "Andres Trujillo", csvCorreo: "", csvTipo: "ASOCIADO", matchDocId: "", matchCollection: "", matchNombre: "", matchCorreo: "" },
  // 4 sin match en Firestore → crear nuevos
  { csvNombre: "Hugo Gutierrez Sandoval", csvCorreo: "gushrealtor@gmail.com", csvTipo: "ASOCIADO", matchDocId: "", matchCollection: "", matchNombre: "", matchCorreo: "" },
  { csvNombre: "Jose Javier Porras Contreras", csvCorreo: "chepejjpc@gmail.com", csvTipo: "ASOCIADO", matchDocId: "", matchCollection: "", matchNombre: "", matchCorreo: "" },
  { csvNombre: "Marco Torres Andrade", csvCorreo: "", csvTipo: "AFILIADO", matchDocId: "", matchCollection: "", matchNombre: "", matchCorreo: "" },
  { csvNombre: "Judith Rivera", csvCorreo: "salesvallarta1@gmail.com", csvTipo: "AFILIADO", matchDocId: "", matchCollection: "", matchNombre: "", matchCorreo: "" },
];

// ============ COMPONENT ============

type ItemStatus = 'pending' | 'processing' | 'done' | 'error' | 'skipped';

interface ItemState {
  status: ItemStatus;
  msg?: string;
}

export default function ReconciliarMiembros() {
  const { user } = useUser();
  const [itemStates, setItemStates] = useState<Map<number, ItemState>>(new Map());
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [duplicatesFound, setDuplicatesFound] = useState<{ docId: string; col: string; nombre: string }[]>([]);

  const addLog = (msg: string) => setLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  const updateItem = (idx: number, state: ItemState) => {
    setItemStates(prev => new Map(prev).set(idx, state));
  };

  const withMatch = RECONCILIATION_DATA.filter(d => d.matchDocId);
  const withoutMatch = RECONCILIATION_DATA.filter(d => !d.matchDocId);

  // ============ EXECUTE RECONCILIATION ============

  const ejecutarReconciliacion = async () => {
    setRunning(true);
    let updated = 0;
    let created = 0;
    let errors = 0;

    addLog('Iniciando reconciliación de 31 miembros...');

    // Step 1: Update correos for the 27 with match
    addLog(`Paso 1: Actualizar correos de ${withMatch.length} miembros...`);

    for (let i = 0; i < RECONCILIATION_DATA.length; i++) {
      const entry = RECONCILIATION_DATA[i];

      if (entry.matchDocId) {
        updateItem(i, { status: 'processing' });
        try {
          await updateDoc(doc(db, entry.matchCollection, entry.matchDocId), {
            correo: entry.csvCorreo.toLowerCase(),
            miembro_oficial: true,
            padron_fecha: serverTimestamp(),
          });
          updateItem(i, { status: 'done', msg: `Correo actualizado: ${entry.matchCorreo} → ${entry.csvCorreo}` });
          updated++;
        } catch (err: any) {
          updateItem(i, { status: 'error', msg: err.message });
          errors++;
        }
      } else if (entry.csvCorreo) {
        // Create new member
        updateItem(i, { status: 'processing' });
        try {
          const col = entry.csvTipo === 'AFILIADO' ? 'asesor' : 'asociado';
          const prefix = col === 'asesor' ? 'ase' : 'aso';
          const initials = entry.csvNombre.split(' ').map(w => w[0]?.toLowerCase() || '').join('').slice(0, 3);
          const randomSuffix = Math.floor(Math.random() * 9000 + 1000);

          const newDoc: any = {
            nombre: entry.csvNombre,
            correo: entry.csvCorreo.toLowerCase(),
            tipo: col === 'asesor' ? 'asesor' : 'asociado',
            activo: true,
            miembro_oficial: true,
            padron_fecha: serverTimestamp(),
            fecha_registro: serverTimestamp(),
            fecha_actualizacion: serverTimestamp(),
            [`${col === 'asesor' ? 'asesor' : 'asociado'}_id`]: `${prefix}_${initials}_${randomSuffix}`,
          };

          if (col === 'asesor') {
            newDoc.inmobiliaria_doc_ids = [];
            newDoc.propiedad_id = [];
          } else {
            newDoc.inmobiliaria_doc_id = '';
          }

          await addDoc(collection(db, col), newDoc);
          updateItem(i, { status: 'done', msg: `Creado como ${col}` });
          created++;
        } catch (err: any) {
          updateItem(i, { status: 'error', msg: err.message });
          errors++;
        }
      } else {
        // No email, skip (Andres Trujillo, Marco Torres)
        updateItem(i, { status: 'skipped', msg: 'Sin correo' });
      }
    }

    // Step 2: Search and clean duplicates created by padron
    addLog('Paso 2: Buscando duplicados creados por el padrón anterior...');

    const dupes: { docId: string; col: string; nombre: string }[] = [];

    for (const entry of withMatch) {
      // Search for duplicates: docs with the OLD correo from CSV that were created as new
      try {
        const col = entry.csvTipo === 'AFILIADO' ? 'asesor' : 'asociado';
        const snap = await getDocs(query(
          collection(db, col),
          where('correo', '==', entry.csvCorreo.toLowerCase())
        ));

        snap.forEach(d => {
          if (d.id !== entry.matchDocId) {
            dupes.push({ docId: d.id, col, nombre: d.data().nombre || entry.csvNombre });
          }
        });

        // Also check the other collection
        const otherCol = col === 'asesor' ? 'asociado' : 'asesor';
        const snap2 = await getDocs(query(
          collection(db, otherCol),
          where('correo', '==', entry.csvCorreo.toLowerCase())
        ));

        snap2.forEach(d => {
          if (d.id !== entry.matchDocId) {
            dupes.push({ docId: d.id, col: otherCol, nombre: d.data().nombre || entry.csvNombre });
          }
        });
      } catch (err) {
        // ignore search errors
      }
    }

    setDuplicatesFound(dupes);
    if (dupes.length > 0) {
      addLog(`Encontrados ${dupes.length} duplicados para eliminar`);
    } else {
      addLog('No se encontraron duplicados');
    }

    addLog(`Reconciliación completa: ${updated} actualizados, ${created} creados, ${errors} errores`);
    setRunning(false);
  };

  // ============ DELETE DUPLICATES ============

  const eliminarDuplicados = async () => {
    addLog(`Eliminando ${duplicatesFound.length} duplicados...`);
    let deleted = 0;
    for (const dupe of duplicatesFound) {
      try {
        await deleteDoc(doc(db, dupe.col, dupe.docId));
        deleted++;
      } catch (err: any) {
        addLog(`Error eliminando ${dupe.nombre}: ${err.message}`);
      }
    }
    addLog(`${deleted} duplicados eliminados`);
    setDuplicatesFound([]);
  };

  // ============ RENDER ============

  const isAdmin = user?.rol === 'ADMIN' || user?.tipo?.includes?.('admin');
  if (!isAdmin) {
    return <div className="container py-5"><h3>Acceso no autorizado</h3></div>;
  }

  const doneCount = Array.from(itemStates.values()).filter(s => s.status === 'done').length;

  return (
    <>
      <UserNav />
      <Breadcrumb items={[
        { label: 'Admin', path: '/admin-dashboard' },
        { label: 'Reconciliar Miembros' }
      ]} />

      <section className="py-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">

              <h4 className="mb-3">
                <i className="fa-solid fa-link me-2 text-primary"></i>
                Reconciliar 31 Miembros Faltantes
              </h4>

              <div className="alert alert-info">
                <strong>Qué hace esta herramienta:</strong>
                <ul className="mb-0 mt-1">
                  <li><strong>27 miembros</strong> existen en Firestore con correo diferente → se actualizará al correo oficial del CSV</li>
                  <li><strong>2 miembros</strong> no existen → se crearán como nuevos (Hugo Gutierrez, Jose Javier, Judith Rivera)</li>
                  <li><strong>2 miembros</strong> sin correo → se omitirán (Andres Trujillo, Marco Torres)</li>
                  <li>Se buscarán y eliminarán duplicados creados por el padrón anterior</li>
                </ul>
              </div>

              {/* Execute button */}
              <button
                className="btn btn-primary btn-lg mb-4"
                onClick={ejecutarReconciliacion}
                disabled={running || doneCount > 0}
              >
                {running ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Procesando...</>
                ) : doneCount > 0 ? (
                  <><i className="fa-solid fa-check me-2"></i>Reconciliación completada ({doneCount}/{RECONCILIATION_DATA.length})</>
                ) : (
                  <><i className="fa-solid fa-play me-2"></i>Ejecutar Reconciliación</>
                )}
              </button>

              {/* Duplicates section */}
              {duplicatesFound.length > 0 && (
                <div className="card border-warning mb-4">
                  <div className="card-header bg-warning bg-opacity-25">
                    <strong>{duplicatesFound.length} duplicados encontrados</strong>
                  </div>
                  <div className="card-body">
                    <ul className="mb-3">
                      {duplicatesFound.map((d, i) => (
                        <li key={i}><small>{d.nombre} ({d.col}/{d.docId})</small></li>
                      ))}
                    </ul>
                    <button className="btn btn-danger" onClick={eliminarDuplicados}>
                      <i className="fa-solid fa-trash me-2"></i>Eliminar {duplicatesFound.length} duplicados
                    </button>
                  </div>
                </div>
              )}

              {/* Table */}
              <div className="table-responsive">
                <table aria-label="Tabla de Reconciliar Miembros" className="table table-sm table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">CSV: Nombre</th>
                      <th scope="col">CSV: Correo (oficial)</th>
                      <th scope="col">Tipo</th>
                      <th scope="col">Firestore: Nombre</th>
                      <th scope="col">Firestore: Correo (actual)</th>
                      <th scope="col">Acción</th>
                      <th scope="col">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RECONCILIATION_DATA.map((entry, i) => {
                      const state = itemStates.get(i);
                      const hasMatch = !!entry.matchDocId;
                      return (
                        <tr key={i} className={
                          state?.status === 'done' ? 'table-success' :
                          state?.status === 'error' ? 'table-danger' :
                          state?.status === 'skipped' ? 'table-light' : ''
                        }>
                          <td>{i + 1}</td>
                          <td><strong>{entry.csvNombre}</strong></td>
                          <td><small>{entry.csvCorreo || <span className="text-danger">Sin correo</span>}</small></td>
                          <td>
                            <span className={`badge ${entry.csvTipo === 'AFILIADO' ? 'bg-info' : 'bg-secondary'}`}>
                              {entry.csvTipo}
                            </span>
                          </td>
                          <td>{entry.matchNombre || <span className="text-muted">-</span>}</td>
                          <td>
                            {hasMatch ? (
                              <small>
                                <span className="text-danger text-decoration-line-through">{entry.matchCorreo}</span>
                                <br />
                                <span className="text-success">{entry.csvCorreo}</span>
                              </small>
                            ) : <span className="text-muted">-</span>}
                          </td>
                          <td>
                            {hasMatch && <span className="badge bg-primary">Actualizar correo</span>}
                            {!hasMatch && entry.csvCorreo && <span className="badge bg-success">Crear nuevo</span>}
                            {!hasMatch && !entry.csvCorreo && <span className="badge bg-secondary">Omitir</span>}
                          </td>
                          <td>
                            {state?.status === 'processing' && <span className="spinner-border spinner-border-sm text-primary"></span>}
                            {state?.status === 'done' && <span className="badge bg-success"><i className="fa-solid fa-check me-1"></i>Listo</span>}
                            {state?.status === 'error' && <span className="badge bg-danger" title={state.msg}>Error</span>}
                            {state?.status === 'skipped' && <span className="badge bg-secondary">Omitido</span>}
                            {!state && <span className="text-muted">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Log */}
              {log.length > 0 && (
                <div className="card mt-4 border-0 bg-dark text-light">
                  <div className="card-header bg-dark border-bottom border-secondary">
                    <small className="text-muted">Log</small>
                  </div>
                  <div className="card-body p-3" style={{ maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {log.map((msg, i) => <div key={i}>{msg}</div>)}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="" />
      <Footer />
    </>
  );
}
